import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCwIcon, SlidersHorizontalIcon, XCircleIcon } from 'lucide-react';

import { api, type ModuleRow } from '@/lib/api';
import { useContent, useLabel, useT, useTn } from '@/lib/i18n';
import { useCannotBecause } from '@/lib/permissions';
import { useUrlState } from '@/lib/url-state';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog } from '@/components/ui/dialog';
import { ConfigEditor } from '@/features/modules/config-editor';
import { BatchRescoreDialog } from '@/features/scoring/batch-rescore';

/**
 * SCORING — *how answers become scores*, one row per module.
 *
 * ### This screen is a return to `09 §Screen 5`, not a new idea
 *
 * That section specifies Scoring as **dimensions, interpretation bands, persona
 * norms, config version, structured editors with a JSON escape hatch, live
 * `validateConfig` feedback, a version panel with diffs, and a re-score
 * action**. Every one of those things was built — and every one was built on
 * **Modules**, by §25 and again by §34q, without either noticing.
 *
 * Meanwhile this route showed three read-only plugin cards that `09` never
 * describes. Its own history explains it: a placeholder reading *"No scoring
 * plugins registered"*, made truthful against `/api/health` when Phase 2
 * falsified the sentence, and never turned into Screen 5. **It got honest
 * without getting a purpose**, and was reported from use as *"there is nothing
 * you can do."*
 *
 * ### Why the subject is the MODULE'S CONFIG and not the engine
 *
 * The obvious reading of that report — put scoring configuration on the engine —
 * is the one thing here that would be wrong, and the reason is load-bearing:
 *
 * - `ScoringConfig` is **embedded in the module**, versioned per module, pinned
 *   per run. Two Big Five modules legitimately carry different bands and norms.
 *   Hanging config off the engine makes one edit rewrite scoring for every
 *   module bound to it — the cascade `plan-beta` §0b exists to prevent.
 * - **An engine has no enabled state.** That only looks 1:1 because the seed
 *   ships three modules, one per model; `11 Flow 2` allows many.
 * - **A plugin cannot be switched off at all**: results pin `instrumentVersion`,
 *   so every retained build must stay resolvable or re-scoring an old run breaks.
 *
 * Once the subject is the module's config, the request works exactly as asked —
 * an unpublished module's scoring row reads unpublished, because what is listed
 * is a module.
 *
 * ### No model is named here
 *
 * `modelType` is rendered as data, never branched on. A per-engine screen is
 * precisely how `harness-viz.test.tsx`'s source scan gets broken, and this file
 * is the one most likely to break it.
 */

/* ------------------------------------------------------- one module's row */

function ScoringRow({
  module,
  onConfigure,
}: {
  module: ModuleRow;
  onConfigure: () => void;
}) {
  const t = useT();
  const tn = useTn();
  const content = useContent();
  const label = useLabel();
  // Authoring a config is a DRAFT-level act (`design:draft`), not a publish.
  const cannot = useCannotBecause('design:draft');

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium">{content(module.name)}</p>
          {/* The module's OWN status, mirrored — this is the honest version of
              "if the module is disabled the scoring shows disabled", and it
              works because the row is a module rather than an engine. */}
          <Badge variant={module.status === 'active' ? 'default' : 'secondary'}>
            {label('moduleStatus', module.status)}
          </Badge>
        </div>
        <p className="text-muted-foreground mt-1 flex flex-wrap gap-x-3 text-xs">
          {/* The engine, as a fact about this module. Monospace and LTR because
              it is an identifier, like every other id on this console. */}
          <span className="font-mono" dir="ltr">{module.modelType}</span>
          <span>{t('modules.configVersion', { version: module.scoringConfigVersion })}</span>
          {/* WHETHER IT IS CALIBRATED — the question §34q made answerable and
              nothing yet displayed. Zero is not an error and is not styled as
              one: it means every result reports a band and no percentile, which
              is accurate. It is rendered only when the API sends the field, so
              an older server says nothing rather than claiming zero. */}
          {module.normGroupCount !== undefined && (
            <span>
              {module.normGroupCount > 0
                ? tn('scoring.normGroups', module.normGroupCount)
                : t('scoring.noNormGroups')}
            </span>
          )}
        </p>
      </div>

      <Button size="sm" variant="outline" onClick={onConfigure} disabled={cannot !== null} title={cannot ?? undefined}>
        <SlidersHorizontalIcon className="size-4" />
        {t('scoring.configure')}
      </Button>
    </div>
  );
}

/* --------------------------------------------------- the engines, demoted */

/**
 * The registered plugins — reference material, at the foot of the screen.
 *
 * This was the whole screen. It is a *version list*: a plugin is sandboxed code
 * registered at boot, and it has no settings, because its settings are the
 * `ScoringConfig` on each module above. Its real audience is somebody creating
 * a module, and the create-module wizard already reads `/api/plugins` for that.
 *
 * Each engine reports the one fact it can honestly give: **how many modules
 * bind it, and how many of those are active.** That is the truthful version of
 * the disabled state that was asked for — an engine has no state of its own,
 * but "backs 2 modules, 1 active" is a real answer to the real question.
 */
function EngineRegistry({ modules }: { modules: ModuleRow[] }) {
  const t = useT();
  const tn = useTn();
  const label = useLabel();
  const { data, isLoading, error } = useQuery({
    queryKey: ['health'],
    queryFn: api.health,
    refetchInterval: 30_000,
  });

  const plugins = data?.plugins ?? [];

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-base">{t('scoring.engines')}</CardTitle>
        <CardDescription>{t('scoring.engines.hint')}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <Skeleton className="h-20 w-full" />}

        {/* The registry is reference material, so an unreachable one is a note
            rather than the error state of the page — the module list above is
            unaffected and still editable. */}
        {error && <p className="text-muted-foreground text-sm">{t('scoring.unreachable')}</p>}

        {data && plugins.length === 0 && (
          <p className="text-destructive text-sm">{t('scoring.empty.description')}</p>
        )}

        {data && plugins.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {plugins.map((modelType) => {
              const bound = modules.filter((m) => m.modelType === modelType);
              const active = bound.filter((m) => m.status === 'active').length;
              const blurb = label('scoring.model', modelType);

              return (
                <div key={modelType} className="rounded-md border p-3">
                  <p className="font-mono text-sm font-medium" dir="ltr">{modelType}</p>
                  {/* A fourth plugin registering itself must not print its own
                      id as its description — `useLabel` echoes an unknown value
                      back, so the echo is what a generic line replaces. */}
                  <p className="text-muted-foreground mt-1 text-xs">
                    {blurb === modelType ? t('scoring.model.unknown') : blurb}
                  </p>
                  <p className="text-muted-foreground mt-2 text-xs">
                    {bound.length === 0
                      ? t('scoring.engine.unused')
                      : `${tn('scoring.engine.modules', bound.length)} · ${t('scoring.engine.active', { count: active })}`}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------- the screen */

export function Scoring() {
  const t = useT();
  const cannotRun = useCannotBecause('assessments:run');
  /**
   * `?moduleId=` — how a module card links straight in (J4), and what makes a
   * filtered view of this screen a URL somebody can paste, the way T3 put every
   * other list's state in the URL.
   */
  const [{ moduleId }, setUrl] = useUrlState({ moduleId: '' });
  const [editing, setEditing] = useState<string | null>(null);
  const [rescoring, setRescoring] = useState(false);

  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });
  const all = modules.data?.items ?? [];
  const shown = moduleId ? all.filter((m) => m.moduleId === moduleId) : all;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t('nav.scoring')}
        description={t('scoring.description')}
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRescoring(true)}
            disabled={cannotRun !== null}
            title={cannotRun ?? undefined}
          >
            <RefreshCwIcon className="size-4" />
            {t('rescore.open')}
          </Button>
        }
      />

      {/* A filtered arrival says so and offers the way out. Without this, a
          card link lands on a one-row screen that looks like the catalogue is
          nearly empty. */}
      {moduleId && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
          <Badge variant="secondary">{t('scoring.filtered', { module: moduleId })}</Badge>
          <Button size="sm" variant="ghost" onClick={() => setUrl({ moduleId: '' })}>
            {t('scoring.showAll')}
          </Button>
        </div>
      )}

      {modules.isLoading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      )}

      {modules.error && (
        <EmptyState
          icon={XCircleIcon}
          title={t('modules.loadFailed')}
          description={
            modules.error instanceof Error ? modules.error.message : t('table.errorDescription')
          }
          actions={<Button onClick={() => void modules.refetch()}>{t('common.retry')}</Button>}
        />
      )}

      {modules.data && shown.length === 0 && (
        <EmptyState
          icon={SlidersHorizontalIcon}
          title={t('scoring.noModules')}
          description={t('scoring.noModules.description')}
        />
      )}

      {shown.length > 0 && (
        <div className="space-y-3">
          {shown.map((module) => (
            <ScoringRow
              key={module.moduleId}
              module={module}
              onConfigure={() => setEditing(module.moduleId)}
            />
          ))}
        </div>
      )}

      {modules.data && <EngineRegistry modules={all} />}

      {/* One dialog mechanism (`10`) — the editor is unchanged and simply lives
          here now rather than on Modules. */}
      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        {editing && <ConfigEditor moduleId={editing} onClose={() => setEditing(null)} />}
      </Dialog>

      <Dialog open={rescoring} onOpenChange={setRescoring}>
        {rescoring && (
          <BatchRescoreDialog
            modules={all}
            {...(moduleId ? { initialModuleId: moduleId } : {})}
            onClose={() => setRescoring(false)}
          />
        )}
      </Dialog>
    </div>
  );
}
